import _ from 'underscore';

import {assert} from 'Asserts';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {
  Capability,
  CAPABILITY_TO_FEATURE_TYPE,
  CAPABILITY_TO_LEGACY_FLAG,
  CapabilityFeature,
  FeatureType,
  ImplementedCapability,
  isStandardFeatureDefinitionWithItemType,
  MAPPED_CAPABILITIES,
  MappedToCapabilityModelType,
  MODEL_TYPE_TO_CAPABILITIES,
  StandardFeatureDefinition,
  UnimplementedCapability,
} from 'auth/capabilities/CapabilityFeatureConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants, {POLYMERS} from 'constants/ModelTypeConstants';
import APIUtil from 'util/APIUtil';

function isMappedToCapabilityModelType(
  modelType: ModelTypeConstants | null
): modelType is MappedToCapabilityModelType {
  if (!modelType) {
    return false;
  }
  return Object.keys(MODEL_TYPE_TO_CAPABILITIES).includes(modelType);
}

function isRegisteredPolymer(
  modelType: MappedToCapabilityModelType,
  itemState: StandardFeatureItemState | undefined
): boolean {
  return POLYMERS.includes(modelType) && Boolean(itemState?.isRegistered);
}

function isCapabilityEnabledForTenant(capability: Exclude<Capability, UnimplementedCapability>): boolean {
  if (BenchlingConfig.IDENTITY_ENABLE_CAPABILITY_GATING) {
    return window.bootstrap?.tenantProvisionableCapabilities?.includes(capability) === true;
  }
  return legacyIsCapabilityEnabled(capability);
}

interface ItemWithOptionalBiologicsOwnership {
  isBiologicsOwned?: boolean | null;
}

export function isBiologicsOwnedItem(item: unknown): boolean {
  return (item as ItemWithOptionalBiologicsOwnership | null | undefined)?.isBiologicsOwned === true;
}

// Actions on registered polymers are only allowed with REGISTRY capability, with the exception of OPEN.
// Because this is a special case, handling it directly is more straightforward than trying to abstract it out.
function getCapabilities(
  modelType: MappedToCapabilityModelType,
  itemState: StandardFeatureItemState | undefined,
  feature: StandardFeature
): ReadonlyArray<Capability> {
  // Biologics ownership is schema-derived, so it must override model-prefix capabilities before
  // registered polymer handling can route the same protein/sequence back to Registry.
  if (isBiologicsOwnedItem(itemState) && isCapabilityEnabledForTenant(Capability.BIOLOGICS)) {
    return [Capability.BIOLOGICS];
  }

  return isRegisteredPolymer(modelType, itemState) && feature !== StandardFeature.OPEN
    ? [Capability.REGISTRY]
    : MODEL_TYPE_TO_CAPABILITIES[modelType];
}

export interface StandardFeatureItemState {
  isRegistered: boolean;
  isBiologicsOwned?: boolean;
}

export function getCapabilityFeatureFromStandardFeature(
  capability: Capability,
  standardFeature: StandardFeature
): CapabilityFeature | null {
  const featureType = CAPABILITY_TO_FEATURE_TYPE[capability as ImplementedCapability];
  const applicationFeatureKey = `${capability}_${standardFeature}`;

  if (Object.keys(featureType).includes(applicationFeatureKey)) {
    return featureType[applicationFeatureKey as keyof FeatureType];
  }

  assert(
    !(MAPPED_CAPABILITIES as Array<Capability>).includes(capability),
    'A StandardFeature was used to gate an item that was ignored for this capability with ' +
      'CAPABILITY_TO_UNUSED_STANDARD_FEATURES. This feature should be implemented or the calling component ' +
      'should prevent this action for this item type.',
    {capability, standardFeature}
  );

  return null;
}

// Given an item's API identifier, a StandardFeature, and (optionally) an item's state, return a list of
// capability-specific features associated with the item and StandardFeature. If the item and StandardFeature
// are not currently supported, return null.
export function getCapabilityFeatures(
  featureDefinition: StandardFeatureDefinition
): Array<CapabilityFeature> | null {
  const {feature} = featureDefinition;

  let modelType: MappedToCapabilityModelType | null = null;
  let itemState: StandardFeatureItemState | undefined;
  if (isStandardFeatureDefinitionWithItemType(featureDefinition)) {
    modelType = featureDefinition.itemType;
  } else {
    const maybeModelType = APIUtil.apiIdToMaybeType(featureDefinition.item.id);

    if (isMappedToCapabilityModelType(maybeModelType)) {
      modelType = maybeModelType;
    }

    itemState = featureDefinition.itemState;
  }

  if (!modelType) {
    return null;
  }

  const capabilities = getCapabilities(modelType, itemState, feature);
  const capabilitiesFeatures = _.compact(
    capabilities.map((capability: Capability) => getCapabilityFeatureFromStandardFeature(capability, feature))
  );
  return capabilitiesFeatures.length ? capabilitiesFeatures : null;
}

export function legacyIsCapabilityEnabled(capability: keyof typeof CAPABILITY_TO_LEGACY_FLAG): boolean {
  const applicationFlag = CAPABILITY_TO_LEGACY_FLAG[capability];

  // If the mapped value is null, there was not a legacy check so we return true
  if (applicationFlag == null) {
    return true;
  }

  const legacyFlagValue = BenchlingConfig[applicationFlag];
  assert(typeof legacyFlagValue === 'boolean', 'applicationFlag must be a bool');
  return Boolean(legacyFlagValue);
}
